// client/src/pages/Game/Battlefield/components/BattleZone.jsx

import React from 'react';
import './BattleZone.css';

/**
 * Total des bonus d'aura (ATK/DEF) reçus par une carte — sert à signaler
 * visuellement les stats boostées (ex. Sylvestus : +2 DEF aux alliés TERRE,
 * terrain, équipements…) sans avoir à ouvrir l'aperçu de la carte source.
 */
const auraBuffTotal = (card, type) =>
  (card.auraBuffs || []).filter(b => b.type === type).reduce((s, b) => s + b.value, 0);

const auraBuffTitle = (card, type) => {
  const buffs = (card.auraBuffs || []).filter(b => b.type === type);
  if (buffs.length === 0) return undefined;
  return buffs
    .map(b => `${b.value >= 0 ? '+' : ''}${b.value} — aura : ${b.sourceName || 'source inconnue'}`)
    .join(' · ');
};

/**
 * Zone de combat — toutes les positions et tailles sont en cqw.
 * 1cqw = 1% de la largeur du battlefield-frame.
 */
const BattleZone = ({
  player,
  gameState,
  position,
  size,
  onZoneClick,
  onDevDragStart,
  devMode,
  getCardElement,
  onCardDragStart
}) => {
  if (!gameState) return null;

  const isPlayerTurn = gameState.activePlayer === 'player';
  const isPlayerBattleZone = player === 'player';
  const playerHasAttack = gameState.playerHasAttack;
  const opponentHasAttack = gameState.opponentHasAttack;
  const pendingAttack = gameState.pendingAttack;

  // Cartes — même gabarit que la zone monstres : hauteur pleine zone
  // (en cqw) + ratio 2:3. Une carte conserve ainsi SA taille quand elle
  // passe de la zone monstres à la zone de combat.
  const cardH = size.height;       // cqw
  const cardW = cardH * (2 / 3);   // ratio 2:3 comme partout
  const gap = 1.2;                 // espacement entre cartes en cqw

  const isDefenseZone = isPlayerBattleZone &&
    !playerHasAttack &&
    opponentHasAttack &&
    pendingAttack && pendingAttack.attackers && pendingAttack.attackers.length > 0;

  const isAttackZone = isPlayerBattleZone && playerHasAttack && isPlayerTurn;
  const isActive = isAttackZone || isDefenseZone || (pendingAttack && isPlayerBattleZone) || devMode;

  // Monstres en combat
  const battleMonsters = player === 'player'
    ? gameState.playerField.filter(c => c.zone === 'battle')
    : gameState.opponentField.filter(c => c.zone === 'battle');

  const opposingAttackers = player === 'player'
    ? gameState.opponentField.filter(c => c.zone === 'battle' && c.role === 'attacker')
    : gameState.playerField.filter(c => c.zone === 'battle' && c.role === 'attacker');

  /**
   * Calcule les positions visuelles (en cqw) d'un ensemble de cartes,
   * centrées horizontalement dans la zone.
   */
  const positionCards = (cards) => {
    if (!cards || cards.length === 0) return [];
    const sorted = [...cards].sort((a, b) => (a.battleOrder ?? a.position) - (b.battleOrder ?? b.position));
    const totalW = sorted.length * cardW + (sorted.length - 1) * gap;
    const startX = (size.width - totalW) / 2;

    return sorted.map((card, i) => ({
      ...card,
      visualPosition: {
        left: `${startX + i * (cardW + gap)}cqw`,
        top: '50%',
        transform: 'translate(0, -50%)'
      }
    }));
  };

  /**
   * Positionne les défenseurs face à leurs attaquants.
   */
  const positionDefenders = (defenders, attackers) => {
    if (!defenders || defenders.length === 0) return [];
    if (!attackers || attackers.length === 0) return positionCards(defenders);

    const sortedAttackers = [...attackers].sort((a, b) => (a.battleOrder ?? a.position) - (b.battleOrder ?? b.position));
    const totalW = sortedAttackers.length * cardW + (sortedAttackers.length - 1) * gap;
    const startX = (size.width - totalW) / 2;

    // Map position → position visuelle de l'attaquant
    const attackerVisualX = {};
    sortedAttackers.forEach((atk, i) => {
      attackerVisualX[atk.position] = startX + i * (cardW + gap);
    });

    return defenders.map(card => {
      const atkPos = card.defendingAgainst !== undefined
        ? card.defendingAgainst
        : (sortedAttackers[0] ? sortedAttackers[0].position : 0);

      const xPos = attackerVisualX[atkPos] !== undefined
        ? attackerVisualX[atkPos]
        : (size.width - cardW) / 2;

      return {
        ...card,
        visualPosition: {
          left: `${xPos}cqw`,
          top: '50%',
          transform: 'translate(0, -50%)'
        }
      };
    });
  };

  // Séparer attaquants et défenseurs pour le positionnement
  const attackers = battleMonsters.filter(c => c.role === 'attacker');
  const defenders = battleMonsters.filter(c => c.role === 'defender');
  const others = battleMonsters.filter(c => !c.role);

  const positionedAttackers = positionCards(attackers);
  const positionedDefenders = defenders.length > 0
    ? positionDefenders(defenders, opposingAttackers)
    : [];
  const positionedOthers = positionCards(others);

  // Fusionner dans l'ordre de rendu
  const allPositioned = [...positionedAttackers, ...positionedDefenders, ...positionedOthers];

  // --- Gestionnaires d'événements ---

  // Une carte de combat du joueur peut être retirée (drag vers la zone
  // monstres) tant que la confirmation n'est pas donnée et que la partie
  // est en cours de déclaration (attaque ou défense).
  const canUndeclareBattleCard = (monster) =>
    player === 'player' &&
    onCardDragStart &&
    monster.role &&
    !gameState.isGameOver &&
    ((gameState.phase === 'battle' && !gameState.pendingAttack?.defenderResponded) ||
      gameState.pendingAction === 'defend');

  const handleBattleCardDragStart = (e, monster) => {
    if (canUndeclareBattleCard(monster)) {
      onCardDragStart(e, { ...monster, location: 'field' });
    }
    e.stopPropagation();
  };

  const handleDevDrag = (e) => {
    if (devMode && onDevDragStart) onDevDragStart(e);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onZoneClick && !devMode) {
      const isDefenseMode = gameState &&
        gameState.activePlayer === 'player' &&
        !gameState.playerHasAttack &&
        gameState.opponentHasAttack &&
        gameState.pendingAttack && gameState.pendingAttack.attackers;

      if (isDefenseMode && player === 'player') {
        const els = document.elementsFromPoint(
          e.clientX || e.changedTouches?.[0]?.clientX || 0,
          e.clientY || e.changedTouches?.[0]?.clientY || 0
        );
        const defEl = els.find(el => el.hasAttribute('data-attacker-position'));
        if (defEl) {
          const atkPos = parseInt(defEl.getAttribute('data-attacker-position'), 10);
          onZoneClick(e, 'battle', { type: 'defense', attackerPosition: atkPos });
        } else if (opposingAttackers.length > 0) {
          onZoneClick(e, 'battle', { type: 'defense', attackerPosition: opposingAttackers[0].position });
        } else {
          onZoneClick(e, 'battle', { type: 'defense' });
        }
      } else if (isAttackZone) {
        onZoneClick(e, 'battle', null);
      } else if (player === 'opponent' && gameState.pendingAttack) {
        onZoneClick(e, 'battle', 'opponent');
      } else {
        onZoneClick(e, 'battle', player === 'opponent' ? 'opponent' : null);
      }
    }
    e.currentTarget.classList.remove('active-drop-target');
  };

  const handleDragOver = (e) => {
    const isDefenseMode = gameState &&
      gameState.activePlayer === 'player' &&
      !gameState.playerHasAttack &&
      gameState.opponentHasAttack &&
      gameState.pendingAttack && gameState.pendingAttack.attackers;

    if ((isDefenseMode && player === 'player') || (isActive && !devMode)) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('active-drop-target');
    }
  };

  const handleDragEnter = (e) => {
    if (isActive && !devMode) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('active-drop-target');
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('active-drop-target');
  };

  // --- Rendu ---

  const getZoneMessage = () => {
    if (isAttackZone) return "Glissez vos monstres ici pour attaquer";
    if (isDefenseZone && opposingAttackers.length > 0) return "DÉFENSE: Glissez vos monstres ici pour bloquer";
    return devMode ? `Zone de combat ${player}` : "";
  };

  // Positions de défense visuelles (slots vides face aux attaquants)
  const renderDefenseSlots = () => {
    if (!isDefenseZone || opposingAttackers.length === 0) return null;

    const sortedAttackers = [...opposingAttackers].sort((a, b) => (a.battleOrder ?? a.position) - (b.battleOrder ?? b.position));
    const defendedPositions = (player === 'player'
      ? gameState.playerField
      : gameState.opponentField
    ).filter(c => c.zone === 'battle' && c.role === 'defender').map(c => c.defendingAgainst);

    const totalW = sortedAttackers.length * cardW + (sortedAttackers.length - 1) * gap;
    const startX = (size.width - totalW) / 2;

    return sortedAttackers.map((attacker, i) => {
      if (defendedPositions.includes(attacker.position)) return null;
      const left = startX + i * (cardW + gap);
      return (
        <div
          key={`def-slot-${attacker.position}`}
          className="defense-position"
          style={{
            width: `${cardW}cqw`,
            height: `${cardH}cqw`,
            position: 'absolute',
            left: `${left}cqw`,
            top: '50%',
            transform: 'translate(0, -50%)',
            zIndex: 5,
          }}
          data-attacker-position={attacker.position}
          data-defense-zone="true"
          onClick={(e) => {
            e.stopPropagation();
            if (onZoneClick) {
              onZoneClick(e, 'battle', { type: 'defense', attackerPosition: attacker.position });
            }
          }}
        >
          {/* R34aa : libellé "Défendre" superflu retiré — la phrase de la
              zone d'attaque suffit à indiquer où poser ses monstres */}
          <div className="vs-label">vs {attacker.position}</div>
        </div>
      );
    }).filter(Boolean);
  };

  return (
    <div
      className={`battle-zone game-element ${player}-battle-zone ${devMode ? 'dev-mode' : ''} ${isActive ? 'active-battle-zone' : ''} ${isAttackZone ? 'attack-zone' : ''} ${isDefenseZone ? 'defense-zone' : ''}`}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        width: `${size.width}cqw`,
        height: `${size.height}cqw`,
        transform: 'translate(-50%, -50%)',
        cursor: devMode ? 'move' : (isActive ? 'pointer' : 'default'),
        zIndex: isActive ? 50 : 15,
      }}
      onMouseDown={devMode ? handleDevDrag : undefined}
      onTouchStart={devMode ? handleDevDrag : undefined}
      onClick={(e) => {
        if (onZoneClick && !devMode) {
          if (isDefenseZone && opposingAttackers.length > 0) {
            onZoneClick(e, 'battle', { type: 'defense', attackerPosition: opposingAttackers[0].position });
          } else if (isAttackZone) {
            onZoneClick(e, 'battle', null);
          } else if (player === 'opponent' && gameState.pendingAttack) {
            onZoneClick(e, 'battle', 'opponent');
          } else {
            onZoneClick(e, 'battle', player === 'opponent' ? 'opponent' : null);
          }
          e.stopPropagation();
        }
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-zone-type="battle"
      data-player={player}
      data-role={isAttackZone ? 'attack' : isDefenseZone ? 'defense' : 'neutral'}
    >
      {isDefenseZone && renderDefenseSlots()}

      {allPositioned.length > 0 ? (
        <div className="battle-monsters-container">
          {allPositioned.map((monster, index) => {
            const roleClass = monster.role === 'attacker' ? 'attacker-card'
              : monster.role === 'defender' ? 'defender-card' : '';
            return (
              <div
                key={`${player}-battle-monster-${index}`}
                className={`battle-monster-card ${roleClass} ${canUndeclareBattleCard(monster) ? 'can-undeclare' : ''} ${monster.locked ? 'fighter-locked' : ''} ${(monster.abilities || []).includes('FIGHTER') ? 'has-fighter' : ''}`}
                style={{
                  width: `${cardW}cqw`,
                  height: `${cardH}cqw`,
                  position: 'absolute',
                  left: monster.visualPosition.left,
                  top: monster.visualPosition.top,
                  transform: monster.visualPosition.transform,
                }}
                onMouseDown={(e) => handleBattleCardDragStart(e, monster)}
                onTouchStart={(e) => handleBattleCardDragStart(e, monster)}
                onClick={(e) => e.stopPropagation()}
                title={canUndeclareBattleCard(monster) ? 'Glissez vers votre zone des monstres pour retirer' : undefined}
                data-monster-id={monster.id}
                data-instance-id={monster.instanceId || monster.id}
                data-position={monster.position}
              >
                <img
                  src={monster.imageUrl || `/api/static/images/cards/${getCardElement?.(monster) || 'fire'}.svg`}
                  alt={monster.name || 'Monster'}
                  onError={(e) => {
                    e.target.src = `/api/static/images/card-backs/neutral.webp`;
                  }}
                />
                <div className="monster-stats">
                  <span
                    className={`attack ${auraBuffTotal(monster, 'ATK') < 0 ? 'stat-lowered' : ''} ${auraBuffTotal(monster, 'ATK') > 0 ? 'stat-buffed' : ''}`}
                    title={auraBuffTitle(monster, 'ATK')}
                  >
                    {monster.attack || 0}
                  </span>
                  {/* BUGFIX (2026-08-18) : PV réels (currentHP), pas la stat de base */}
                  <span
                    className={`defense ${(monster.currentHP ?? monster.maxHP ?? monster.defense) < (monster.maxHP ?? monster.defense) ? 'damaged' : ''} ${auraBuffTotal(monster, 'DEF') > 0 ? 'stat-buffed' : ''}`}
                    title={[
                      auraBuffTitle(monster, 'DEF'),
                      `PV : ${monster.currentHP ?? '?'} / ${monster.maxHP ?? '?'}`,
                    ].filter(Boolean).join(' — ')}
                  >
                    {(monster.currentHP ?? monster.defense) || 0}
                  </span>
                </div>
                {monster.role && (
                  <div className={`card-role ${monster.role}`}>
                    {monster.role === 'attacker' ? 'ATK' : 'DEF'}
                  </div>
                )}
                {(monster.abilities || []).includes('FIGHTER') && (
                  <div className="fighter-badge" title="Combattant : peut forcer un monstre adverse à défendre">⚔</div>
                )}
                {(monster.abilities || []).some(a => a === 'STEALTH' || a === 'FLIGHT') && (
                  <div className="stealth-badge" title="Insaisissable : seul un monstre avec Discrétion/Vol peut le bloquer — sinon il attaque directement">🌫</div>
                )}
                {monster.locked && (
                  <div className="locked-badge" title="Forcé de défendre par Combattant — retrait impossible">⛓</div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="battle-zone-message">{getZoneMessage()}</div>
      )}
    </div>
  );
};

export default BattleZone;
